/**
 * File integrity fingerprints (Faz 9.1/9.4, OCS `FileFingerprint::capture`).
 *
 * Full-file sha256 is too slow for multi-megabyte documents on every save,
 * so the fast path hashes only the head bytes and mixes in the file length —
 * the OCS FileFingerprint pattern (head sample + size). `crypto.subtle` is
 * available in the Tauri webview (secure context) and every modern browser;
 * a tiny FNV-1a fallback keeps the module working where it is not.
 */

/** Head sample window for the fast fingerprint (OCS: sample hash + length). */
const FINGERPRINT_HEAD_BYTES = 1_048_576;

export interface FileFingerprint {
    size: number;
    lastModified: number;
    /** sha256 hex of the first head bytes (mixed with size), or FNV fallback. */
    headSha256: string;
    /** Which hash path produced the value ('sha256' | 'fnv1a'). */
    algorithm: 'sha256' | 'fnv1a';
}

async function sha256Hex(bytes: Uint8Array): Promise<string | null> {
    try {
        if (typeof crypto === 'undefined' || !crypto.subtle) return null;
        const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer);
        return Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    } catch {
        return null;
    }
}

/** FNV-1a 64-bit-ish (two 32-bit lanes) hex — fallback hash path. */
function fnv1aHex(bytes: Uint8Array): string {
    let hi = 0x84222325;
    let lo = 0xcbf29ce4;
    for (const b of bytes) {
        lo ^= b;
        lo = Math.imul(lo, 0x01000193) >>> 0;
        hi ^= b;
        hi = Math.imul(hi, 0x01000193) >>> 0;
    }
    return (hi >>> 0).toString(16).padStart(8, '0') + lo.toString(16).padStart(8, '0');
}

/** Computes the head+length fingerprint of any Blob/File. */
export async function captureFileFingerprint(blob: Blob): Promise<FileFingerprint> {
    const head = blob.slice(0, Math.min(FINGERPRINT_HEAD_BYTES, blob.size));
    const bytes = new Uint8Array(await head.arrayBuffer());
    const sha = await sha256Hex(bytes);
    return {
        size: blob.size,
        // Blob has no mtime; File (the only caller shape here) does.
        lastModified: (blob as File).lastModified ?? 0,
        headSha256: sha ?? fnv1aHex(bytes),
        algorithm: sha ? 'sha256' : 'fnv1a',
    };
}

/** Full-file sha256 hex (recovery-report field; fine for small files). */
export async function sha256FileHex(bytes: Uint8Array): Promise<string | null> {
    return sha256Hex(bytes);
}

/** True when both fingerprints were captured from identical content. */
export function fingerprintsMatch(a: FileFingerprint | null, b: FileFingerprint | null): boolean {
    if (!a || !b) return false;
    if (a.size !== b.size) return false;
    if (a.headSha256 === b.headSha256) return true;
    // Different algorithm mix (hash path changed mid-session) cannot be
    // compared safely — treat as a match to avoid a false external-change
    // alarm; size + mtime already disagreed silently in that case.
    if (a.algorithm !== b.algorithm) return true;
    return false;
}
